import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type LeadStatus = "novo" | "em_contato" | "convertido" | "descartado";

export interface Lead {
  id: string;
  nome: string;
  email: string | null;
  whatsapp: string | null;
  cidade: string | null;
  estabelecimento: string | null;
  tipo: string | null;
  mensagem: string | null;
  status: LeadStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export function useLeads() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLeads = useCallback(async () => {
    const { data, error } = await supabase
      .from("leads")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast.error("Erro ao carregar leads: " + error.message);
    } else {
      setLeads((data || []) as Lead[]);
    }
    setLoading(false);
  }, []);

  const updateLeadStatus = useCallback(async (id: string, status: LeadStatus) => {
    const { error } = await supabase
      .from("leads")
      .update({ status })
      .eq("id", id);
    if (error) {
      toast.error("Erro ao atualizar status: " + error.message);
      return false;
    }
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    toast.success("Status atualizado");
    return true;
  }, []);

  const updateLeadNotes = useCallback(async (id: string, notes: string) => {
    const { error } = await supabase
      .from("leads")
      .update({ notes })
      .eq("id", id);
    if (error) {
      toast.error("Erro ao salvar anotações: " + error.message);
      return false;
    }
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, notes } : l)));
    toast.success("Anotações salvas");
    return true;
  }, []);

  const deleteLead = useCallback(async (id: string) => {
    const { error } = await supabase.from("leads").delete().eq("id", id);
    if (error) {
      toast.error("Erro ao excluir lead: " + error.message);
      return false;
    }
    setLeads((prev) => prev.filter((l) => l.id !== id));
    toast.success("Lead excluído");
    return true;
  }, []);

  useEffect(() => {
    fetchLeads();

    const channel = supabase
      .channel("leads-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "leads" },
        () => {
          fetchLeads();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchLeads]);

  return { leads, loading, fetchLeads, updateLeadStatus, updateLeadNotes, deleteLead };
}
